import {
  createLicenseType,
  getLicenseType,
  updateLicenseType,
} from "@/hera-api/client";
import { getLicenseTypesOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface LicenseTypeFormData {
  name: string;
  requires_property: boolean;
  effective_date_label: string;
  effective_end_date_label: string;
  is_deleted: boolean;
}

export function useLicenseTypeForm(licenseTypeId: string | null) {
  const queryClient = useQueryClient();

  // This query is not used in this hook, removing it

  const {
    data: singleLicenseTypeData,
    isLoading: isLoadingSingle,
    error: errorSingle,
  } = useQuery({
    queryKey: ["license-type", licenseTypeId, { include_deleted: true }],
    queryFn: () =>
      getLicenseType({
        path: { license_type_id: licenseTypeId || "" },
        query: { include_deleted: true },
      }),
    enabled: !!licenseTypeId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await createLicenseType({ body: data });
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions({ query: { include_deleted: true } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions().queryKey,
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: { id: string; data: Partial<LicenseTypeFormData> }) => {
      const response = await updateLicenseType({
        path: { license_type_id: id },
        body: data,
      });
      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions({ query: { include_deleted: true } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions().queryKey,
      });
      if (licenseTypeId) {
        queryClient.invalidateQueries({
          queryKey: ["license-type", licenseTypeId, { include_deleted: true }],
        });
      }
    },
  });

  // Get the license type data for the form
  const currentLicenseType =
    licenseTypeId && singleLicenseTypeData?.data
      ? singleLicenseTypeData.data
      : null;

  // Transform the data for the form
  const formData: LicenseTypeFormData = currentLicenseType
    ? {
        name: currentLicenseType.name || "",
        requires_property: currentLicenseType.requires_property || false,
        effective_date_label: currentLicenseType.effective_date_label || "",
        effective_end_date_label:
          currentLicenseType.effective_end_date_label || "",
        is_deleted: !!currentLicenseType.deleted_at,
      }
    : {
        name: "",
        requires_property: false,
        effective_date_label: "",
        effective_end_date_label: "",
        is_deleted: false,
      };

  const handleSubmit = async (data: LicenseTypeFormData) => {
    try {
      if (licenseTypeId) {
        // Update existing license type
        const updateData: any = {
          name: data.name,
          requires_property: data.requires_property,
          effective_date_label: data.effective_date_label,
          effective_end_date_label: data.effective_end_date_label,
        };

        // Handle soft delete
        if (
          currentLicenseType &&
          data.is_deleted !== !!currentLicenseType.deleted_at
        ) {
          updateData.is_deleted = data.is_deleted;
        }

        await updateMutation.mutateAsync({
          id: licenseTypeId,
          data: updateData,
        });
      } else {
        // Create new license type
        await createMutation.mutateAsync({
          name: data.name,
          requires_property: data.requires_property,
          effective_date_label: data.effective_date_label,
          effective_end_date_label: data.effective_end_date_label,
        });
      }
    } catch (error) {
      console.error("License type form submission error:", error);
      throw error;
    }
  };

  return {
    licenseTypeData: formData,
    isLoading: isLoadingSingle,
    error: errorSingle,
    handleSubmit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}
